import { useQuery } from '@tanstack/react-query';
import { useAppCustomers } from '@/hooks';
import { QUERY_KEYS } from '../queryKeys';
import { operationsDashboard } from './OperationsDashboard';

const useScope = () => {
  const { customerIds, locationIds } = useAppCustomers();
  return [customerIds, locationIds] as const;
};

export const useKpiSummary = () => {
  const scope = useScope();
  return useQuery({
    queryKey: [QUERY_KEYS.dashboardKpiSummary, ...scope],
    queryFn: async () => (await operationsDashboard.getKpiSummary()).data,
  });
};

export const useThroughputTrend = (weeks = 8) => {
  const scope = useScope();
  return useQuery({
    queryKey: [QUERY_KEYS.dashboardThroughputTrend, weeks, ...scope],
    queryFn: async () =>
      (await operationsDashboard.getThroughputTrend({ weeks })).data,
  });
};

export const useStockByCompound = () => {
  const scope = useScope();
  return useQuery({
    queryKey: [QUERY_KEYS.dashboardStockByCompound, ...scope],
    queryFn: async () => (await operationsDashboard.getStockByCompound()).data,
  });
};

export const useStockByModel = (top = 15) => {
  const scope = useScope();
  return useQuery({
    queryKey: [QUERY_KEYS.dashboardStockByModel, top, ...scope],
    queryFn: async () =>
      (await operationsDashboard.getStockByModel({ top })).data,
  });
};

export const useStockByStatus = () => {
  const scope = useScope();
  return useQuery({
    queryKey: [QUERY_KEYS.dashboardStockByStatus, ...scope],
    queryFn: async () => (await operationsDashboard.getStockByStatus()).data,
  });
};

export const useEventHeatmap = () => {
  const scope = useScope();
  return useQuery({
    queryKey: [QUERY_KEYS.dashboardEventHeatmap, ...scope],
    queryFn: async () => (await operationsDashboard.getEventHeatmap()).data,
  });
};

export const useLoadPullingSummary = (siteId?: number, customerId?: number) => {
  const scope = useScope();
  return useQuery({
    queryKey: [
      QUERY_KEYS.dashboardLoadPullingSummary,
      siteId,
      customerId ?? 'all',
      ...scope,
    ],
    queryFn: async () =>
      (await operationsDashboard.getLoadPullingSummary(siteId!, customerId))
        .data,
    enabled: Boolean(siteId),
  });
};

export const useLoadPullingOverview = () => {
  const scope = useScope();
  return useQuery({
    queryKey: [QUERY_KEYS.dashboardLoadPulling, ...scope],
    queryFn: async () =>
      (await operationsDashboard.getLoadPullingOverview()).data,
  });
};

export const useMaintenanceSummary = (weeks = 6) => {
  const scope = useScope();
  return useQuery({
    queryKey: [QUERY_KEYS.dashboardMaintenance, weeks, ...scope],
    queryFn: async () =>
      (await operationsDashboard.getMaintenance({ weeks })).data,
  });
};
